import { observer } from "mobx-react";
import "./OfferItem.scss";
import { Offer, OfferStatus } from "../../../../stores/OfferStore/types";
import { LawyerInfo } from "../../../../components/LawyerInfo/LawyerInfo";
import useTranslation from "../../../../hooks/useTranslation";
import { Alert, Button, CircularProgress, TextField } from "@mui/material";
import * as React from "react";
import { useState } from "react";
import offerStore from "../../../../stores/OfferStore";
import feedbackStore from "../../../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../../../stores/FeedbackStore/types";

const OfferItem = observer(({ offer }: { offer: Offer }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [error, setError] = useState("");

  const validate = (value: string): boolean => {
    if (value.length < 10) {
      setError("reject-reason-minlength");
      return false;
    } else if (value.length > 100) {
      setError("reject-reason-maxlength");
      return false;
    } else {
      setError("");
      return true;
    }
  };

  const handleRejectReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRejectReason(e.target.value);
    validate(e.target.value);
  };

  const deleteOffer = () => {
    const isValid = validate(rejectReason);

    if (isValid) {
      setLoading(true);
      offerStore
        .updateOfferStatus(offer, OfferStatus.REJECTED, rejectReason)
        .then(() => (offer.status = OfferStatus.REJECTED))
        .catch((error) => {
          console.error(error);
          feedbackStore.setShowFeedback(true, {
            severity: FeedbackSeverity.ERROR,
            message: "connection-error",
          });
        })
        .finally(() => setLoading(false));
    }
  };

  const activateOffer = () => {
    setLoading(true);
    offerStore
      .updateOfferStatus(offer, OfferStatus.PENDING)
      .then(() => (offer.status = OfferStatus.PENDING))
      .catch((error) => {
        console.error(error);
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "connection-error",
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className={`dashboardItem offerItem ${offer.status?.toLowerCase()}`}>
      <header>{LawyerInfo(offer, t)}</header>
      <main>
        <p>{offer.summary}</p>
      </main>
      <footer>
        {offer.status !== OfferStatus.REJECTED ? (
          <>
            <TextField
              sx={{ flex: 3 }}
              error={!!error}
              onChange={handleRejectReasonChange}
              size={"small"}
            />
            <Button
              sx={{ flex: 1 }}
              disabled={loading || !!error}
              variant={"contained"}
              startIcon={<i className="fal fa-trash" />}
              color={"error"}
              size={"small"}
              onClick={deleteOffer}
            >
              {loading ? <CircularProgress size={"16px"} /> : t("delete")}
            </Button>
          </>
        ) : (
          <>
            <Button
              fullWidth
              disabled={loading}
              variant={"contained"}
              color={"secondary"}
              onClick={activateOffer}
            >
              {loading ? <CircularProgress size={"16px"} /> : t("activate")}
            </Button>
          </>
        )}
      </footer>
      <section>
        {error !== "" && (
          <div className={"pt"}>
            <Alert
              severity={"error"}
              sx={{
                width: { xs: "100%", sm: "268px" },
                boxSizing: "border-box",
              }}
            >
              {t(error)}
            </Alert>
          </div>
        )}
      </section>
    </div>
  );
});

export default OfferItem;
